import React from 'react';
import { Button, ButtonProps, Card } from '@fluentui/react-components';

import { CardData } from 'src/models/CardActivities/CardData';
import { CardTemplate, IconTopicType, LinkBehavior } from 'src/utils/enums';
import CardHalfBackgroundLayout from 'src/components/molecules/card-half-background-layout/CardHalfBackgroundLayout';
import { ERROR_MESSAGES, ICON_TOPIC_BACKGROUND_COLOR, ICON_TOPIC_CONFIG } from 'src/utils/constants';
import CircularIcon from 'src/components/atoms/circular-icon/CircularIcon';
import EnhancedButton from 'src/components/atoms/enhanced-button/EnhancedButton';
import { openExternalUrl } from 'src/utils/utils';
import EnhancedDialog from 'src/components/molecules/enhanced-dialog/EnhancedDialog';
import CardFullImageLayout from '../../molecules/card-full-image-layout/CardFullImageLayout';
import CardFullColorLayout from '../../molecules/card-full-color-layout/CardFullColorLayout';
import CardTextOnlyLayout from '../../molecules/card-text-only-layout/CardTextOnlyLayout';
import CardChallengeLayout from '../../molecules/card-challenge-layout/CardChallengeLayout';
import CardStepBattleLayout from '../../molecules/card-step-battle-layout/CardStepBattleLayout';
import { useStyles } from './ThingsWorkingOnCard.styles';

type Props = {
  card: CardData
  ariaLabel?: string
};

const ThingsWorkingOnCard: React.FC<Props> = ({ card, ariaLabel }) => {
  const styles = useStyles();
  const { templateData, template } = card;
  const {
    category,
    headline,
    color,
    body,
    image = '',
    icon,
    topLine,
    nameRow1: sectionLabel,
    nameRow2: title,
    nameRow3: caption,
  } = templateData ?? {};

  const {
    modalHeadline,
    modalContent,
    callToAction,
    callToAction2,
    actionURL,
    actionURL2,
    linkBehavior,
    linkBehavior2,
  } = templateData ?? {};

  const renderIcon = (): React.ReactNode => {
    const iconTopicType = icon;
    if (!iconTopicType) return null;

    const config = ICON_TOPIC_CONFIG[iconTopicType as IconTopicType];
    if (!config) throw new Error(ERROR_MESSAGES.invalidIconTopicType(iconTopicType));

    const { icon: Icon, color: iconColor } = config;

    return (
      <CircularIcon
        icon={(
          <span className={styles.icon}>
            <Icon color={iconColor} />
          </span>
        )}
        backgroundColor={ICON_TOPIC_BACKGROUND_COLOR}
        className={styles.iconContainer}
      />
    );
  };

  const renderCardActivity = (): React.ReactNode => {
    switch (template) {
      case CardTemplate.FULL_IMAGE:
      case CardTemplate.DAILY_HABITS:
      case CardTemplate.RIGHT_SIDE_VIDEO:
        return <CardFullImageLayout imageUrl={image} headline={headline} body={body} />;

      case CardTemplate.FULL_COLOR:
        return <CardFullColorLayout category={category} color={color} headline={headline} body={body} />;

      case CardTemplate.TEXT_ONLY:
        return <CardTextOnlyLayout headline={headline} color={color} body={body} iconUrl={icon} />;

      case CardTemplate.CHALLENGES:
        return (
          <CardChallengeLayout
            imageUrl={image}
            headline={headline}
            body={body}
            sectionLabel={sectionLabel}
            title={title}
            caption={caption}
          />
        );

      case CardTemplate.STEP_BATTLE:
        return <CardStepBattleLayout headline={headline} body={body} />;

      case CardTemplate.HALF_IMAGE:
      case CardTemplate.RIGHT_SIDE_IMAGE:
        return (
          <CardHalfBackgroundLayout
            headline={headline}
            image={image}
            body={body}
            cardTemplate={template}
            icon={renderIcon()}
            topLine={topLine}
          />
        );

      default:
        return <CardHalfBackgroundLayout headline={headline} image={image} body={body} cardTemplate={template} />;
    }
  };

  const renderButton = (params: {
    linkBehavior?: LinkBehavior
    callToAction?: string
    link?: string
    dialogTitle?: string
    dialogContent?: string
    appearance?: ButtonProps['appearance']
    dataTestId?: string
  }): React.ReactNode => {
    const { linkBehavior, callToAction, link, dialogTitle, dialogContent, appearance, dataTestId } = params;

    if (!callToAction?.trim()) return null;

    const behavior = (!linkBehavior && !!link) ? LinkBehavior.OpenUrl : linkBehavior;

    switch (behavior) {
      case LinkBehavior.OpenUrl: {
        const handleClick = () => link?.trim() && openExternalUrl(link.trim());
        return <EnhancedButton onClick={handleClick} isExternal appearance={appearance} data-testid={dataTestId}>{callToAction}</EnhancedButton>;
      }

      case LinkBehavior.OpenTextModal: {
        return (
          <EnhancedDialog dialogTitle={dialogTitle!} dialogContent={dialogContent!}>
            <Button appearance={appearance} data-testid={dataTestId}>{callToAction}</Button>
          </EnhancedDialog>
        );
      }

      default: {
        return <div data-testid={dataTestId}>{callToAction}</div>;
      }
    }
  };

  const firstButton = renderButton({
    linkBehavior: linkBehavior,
    callToAction: callToAction,
    link: actionURL,
    dialogTitle: modalHeadline,
    dialogContent: modalContent,
    appearance: 'primary',
    dataTestId: 'primary-button',
  });

  const secondButton = renderButton({
    linkBehavior: linkBehavior2,
    callToAction: callToAction2,
    link: actionURL2,
    dialogTitle: modalHeadline,
    dialogContent: modalContent,
    appearance: 'secondary',
    dataTestId: 'secondary-button',
  });

  return (
    <Card className={styles.card} aria-label={ariaLabel} data-testid="things-working-on-card">
      <div className={styles.body}>{renderCardActivity()}</div>

      <div className={styles.footer} data-testid="things-working-on-card-footer">
        {firstButton}

        {secondButton}
      </div>
    </Card>
  );
};

export default ThingsWorkingOnCard;
